import React, { useCallback, useContext, useMemo, useRef } from 'react';

import { type ForYouConfig } from '@atlassian/global-side-navigation/types';

import { HOME, WIKI } from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	GO_HOME_SHORTCUT,
	GO_TO_RECENT_SHORTCUT,
	GO_TO_STARRED_SHORTCUT,
	UNIVERSAL_GO_HOME_SHORTCUT,
	UNIVERSAL_GO_TO_RECENT_SHORTCUT,
	UNIVERSAL_GO_TO_STARRED_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { LoadableLazy } from '@confluence/loadable';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { HomeOnboardingTakeoverV2HomeNudge } from '@confluence/experiment-home-onboarding-takeover-v2/entry-points/HomeOnboardingTakeoverV2HomeNudge.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { EditorForYouSpotlight } from '@confluence/experiment-nth-user-orientation/entry-points/EditorForYouSpotlight.ts';
import { APP_NAV_PAGES_DROPDOWN_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStop,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';

import { KeyboardShortcutWrapper } from '../KeyboardShortcutWrapper.tsx';
import { useRecentItems, useRecentsFilterConfig } from './useRecentItems.tsx';
import { useStarredItems } from './useStarredItems.tsx';
import { preloadStarredContentMenu } from '../../ContentMenu/preloadStarredContentMenu.ts';
import { preloadRecentContentMenu } from '../../ContentMenu/preloadRecentContentMenu.ts';

import { DiscoErrorBoundary } from '../components/DiscoErrorBoundary.tsx';

const recentAttributes = { navVersion: '4', dropdownType: 'recent' };
const starredAttributes = { navVersion: '4', dropdownType: 'starred' };
export const StarredContentMenuLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-StarredContentMenu" */
				'../../ContentMenu/StarredContentMenu.tsx'
			)
		).StarredContentMenu,
});

export const RecentContentMenuLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-RecentContentMenu" */
				'../../ContentMenu/RecentContentMenu.tsx'
			)
		).RecentContentMenu,
});

const StopRecent = () => {
	return <ExperienceStop name={APP_NAV_PAGES_DROPDOWN_EXPERIENCE} attributes={recentAttributes} />;
};

const StopStarred = () => (
	<ExperienceStop name={APP_NAV_PAGES_DROPDOWN_EXPERIENCE} attributes={starredAttributes} />
);

export const useForYouConfig = (): ForYouConfig => {
	const { cloudId } = useSessionData();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const homeUrl = HOME.toUrl();
	const isHomeSelected = useRouteUrl(isOnHomeSelector);

	const isRecentOpenRef = useRef(false);
	const recentsFilterConfig = useRecentsFilterConfig();
	const recentViewAllUrl = HOME.toUrl({ view: 'recent' });
	const isRecentSelected = useRouteUrl(isOnRecentRoute);
	const onRecentMouseEnter = useCallback(() => {
		if (!isRecentOpenRef.current) {
			void RecentContentMenuLoader.preload();
			void preloadRecentContentMenu(cloudId);
		}
	}, [cloudId]);

	const isStarredOpenRef = useRef(false);
	const starredViewAllUrl = HOME.toUrl({ view: 'starred' });
	const isStarredSelected = useRouteUrl(isOnStarredRoute);
	const onStarredMouseEnter = useCallback(() => {
		if (!isStarredOpenRef.current) {
			void StarredContentMenuLoader.preload();
			void preloadStarredContentMenu();
		}
	}, []);

	const experienceTracker = useContext(ExperienceTrackerContext);

	const startExperienceTracker = useCallback(
		(experienceName) => {
			experienceTracker.start({
				name: APP_NAV_PAGES_DROPDOWN_EXPERIENCE,
				timeout: ExperienceTimeout.NAVIGATION_LOAD,
				attributes: {
					navVersion: '4',
					dropdownType: experienceName,
				},
			});
		},
		[experienceTracker],
	);

	return useMemo<ForYouConfig>(
		() => ({
			forYouItem: {
				type: 'link' as const,
				menuItemId: 'yourWork',
				url: homeUrl,
				isSelected: isHomeSelected,
				ItemWrapper: ({ children }: { children: React.ReactNode }) => {
					return (
						<DiscoErrorBoundary>
							<KeyboardShortcutWrapper
								href={homeUrl}
								deprecatedKeyboardShortcut={
									!(isLiveEditMode || isOnEditRoute) ? GO_HOME_SHORTCUT : undefined
								}
								keyboardShortcut={UNIVERSAL_GO_HOME_SHORTCUT}
								isAppNavigationShortcut
							>
								<HomeOnboardingTakeoverV2HomeNudge>
									<EditorForYouSpotlight>{children}</EditorForYouSpotlight>
								</HomeOnboardingTakeoverV2HomeNudge>
							</KeyboardShortcutWrapper>
						</DiscoErrorBoundary>
					);
				},
			},
			recent: {
				menuItemId: 'recent',
				useItems: useRecentItems,
				viewAllUrl: recentViewAllUrl,
				isSearchable: true,
				filterConfig: recentsFilterConfig,
				isSelected: isRecentSelected,
				children: <RecentContentMenuLoader />,
				experienceEndTracker: StopRecent,
				onOpenChange: (isOpen: boolean) => {
					if (isOpen) {
						startExperienceTracker('recent');
					}
					isRecentOpenRef.current = isOpen;
				},
				ItemWrapper: ({ children }: { children: React.ReactNode }) => {
					return (
						<DiscoErrorBoundary>
							<div onMouseEnter={onRecentMouseEnter} onFocus={onRecentMouseEnter}>
								<KeyboardShortcutWrapper
									deprecatedKeyboardShortcut={
										!(isLiveEditMode || isOnEditRoute) ? GO_TO_RECENT_SHORTCUT : undefined
									}
									keyboardShortcut={UNIVERSAL_GO_TO_RECENT_SHORTCUT}
									isAppNavigationShortcut
								>
									{children}
								</KeyboardShortcutWrapper>
							</div>
						</DiscoErrorBoundary>
					);
				},
			},
			starred: {
				menuItemId: 'starred',
				useItems: useStarredItems,
				viewAllUrl: starredViewAllUrl,
				isSearchable: true,
				isSelected: isStarredSelected,
				children: <StarredContentMenuLoader />,
				experienceEndTracker: StopStarred,
				onOpenChange: (isOpen: boolean) => {
					if (isOpen) {
						startExperienceTracker('starred');
					}
					isStarredOpenRef.current = isOpen;
				},
				ItemWrapper: ({ children }: { children: React.ReactNode }) => {
					return (
						<DiscoErrorBoundary>
							<div onMouseEnter={onStarredMouseEnter} onFocus={onStarredMouseEnter}>
								<KeyboardShortcutWrapper
									deprecatedKeyboardShortcut={
										!(isLiveEditMode || isOnEditRoute) ? GO_TO_STARRED_SHORTCUT : undefined
									}
									keyboardShortcut={UNIVERSAL_GO_TO_STARRED_SHORTCUT}
									isAppNavigationShortcut
								>
									{children}
								</KeyboardShortcutWrapper>
							</div>
						</DiscoErrorBoundary>
					);
				},
			},
		}),
		[
			homeUrl,
			isHomeSelected,
			isRecentSelected,
			isStarredSelected,
			recentViewAllUrl,
			recentsFilterConfig,
			starredViewAllUrl,
			isLiveEditMode,
			isOnEditRoute,
			onRecentMouseEnter,
			onStarredMouseEnter,
			startExperienceTracker,
		],
	);
};

const isOnHomeSelector = {
	selector: (routeUrl: string | undefined) => {
		if (!routeUrl) {
			return false;
		}
		return routeUrl === HOME.toUrl() || routeUrl === WIKI.toUrl();
	},
};
const isOnStarredRoute = {
	selector: (routeUrl: string | undefined) => routeUrl === HOME.toUrl({ view: 'starred' }),
};
const isOnRecentRoute = {
	selector: (routeUrl: string | undefined) => routeUrl === HOME.toUrl({ view: 'recent' }),
};
